import { parseISO } from 'date-fns';
import { formatDistanceToNow } from 'date-fns';
import { parseDateInput } from '../lib/dateParsing';
import {
  isDateOnlyString,
} from './hstDateInput';

export const HST_TIMEZONE = 'Pacific/Honolulu';

/**
 * Get the hour component (0-23) of a date in HST timezone
 */
export function getHstHour(date: Date): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: HST_TIMEZONE,
    hour: 'numeric',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const hourPart = parts.find((p) => p.type === 'hour');
  return parseInt(hourPart?.value ?? '0', 10);
}

/**
 * Get the minute component (0-59) of a date in HST timezone
 */
export function getHstMinute(date: Date): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: HST_TIMEZONE,
    minute: 'numeric',
  });
  const parts = formatter.formatToParts(date);
  const minutePart = parts.find((p) => p.type === 'minute');
  return parseInt(minutePart?.value ?? '0', 10);
}

/**
 * Get the total minutes since midnight in HST timezone
 */
export function getHstMinutesSinceMidnight(date: Date): number {
  return getHstHour(date) * 60 + getHstMinute(date);
}

/**
 * Get the HST date components (year, month, day) for a given date
 */
export function getHstDateParts(date: Date): { year: number; month: number; day: number } {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: HST_TIMEZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
  const parts = formatter.formatToParts(date);
  const year = parseInt(parts.find((p) => p.type === 'year')?.value ?? '0', 10);
  const month = parseInt(parts.find((p) => p.type === 'month')?.value ?? '1', 10);
  const day = parseInt(parts.find((p) => p.type === 'day')?.value ?? '1', 10);
  return { year, month, day };
}

/**
 * Get the start of day (midnight) in HST timezone as a UTC Date
 * HST is UTC-10, so midnight HST = 10:00 UTC
 */
export function startOfHstDay(date: Date): Date {
  const { year, month, day } = getHstDateParts(date);
  // Create a date at midnight HST, which is 10:00 UTC
  // HST is always UTC-10 (no daylight saving)
  const utcDate = new Date(Date.UTC(year, month - 1, day, 10, 0, 0, 0));
  return utcDate;
}

/**
 * Get the end of day (23:59:59.999) in HST timezone as a UTC Date
 * HST is UTC-10, so 23:59:59 HST = 09:59:59 UTC next day
 */
export function endOfHstDay(date: Date): Date {
  const { year, month, day } = getHstDateParts(date);
  // Create a date at 23:59:59.999 HST, which is 09:59:59.999 UTC next day
  const utcDate = new Date(Date.UTC(year, month - 1, day + 1, 9, 59, 59, 999));
  return utcDate;
}

/** Create an instant from explicit HST calendar components. */
export function createHstDate(
  year: number,
  monthIndex: number,
  day: number,
  hour = 0,
  minute = 0,
): Date {
  return new Date(Date.UTC(year, monthIndex, day, hour + 10, minute, 0, 0));
}

/** Return a stable YYYY-MM-DD HST day key for an instant. */
export function getHstDayKey(date: Date): string {
  const { year, month, day } = getHstDateParts(date);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function parseHstDayKey(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  return createHstDate(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export function addHstDays(date: Date, days: number): Date {
  const { year, month, day } = getHstDateParts(date);
  return createHstDate(year, month - 1, day + days);
}

export function addHstMonths(date: Date, months: number): Date {
  const { year, month, day } = getHstDateParts(date);
  const targetFirst = createHstDate(year, month - 1 + months, 1);
  const target = getHstDateParts(targetFirst);
  const lastDay = getHstDateParts(createHstDate(target.year, target.month, 0)).day;
  return createHstDate(target.year, target.month - 1, Math.min(day, lastDay));
}

export function setHstTime(date: Date, hour: number, minute = 0): Date {
  const { year, month, day } = getHstDateParts(date);
  return createHstDate(year, month - 1, day, hour, minute);
}

export function startOfHstWeek(date: Date): Date {
  const start = startOfHstDay(date);
  return addHstDays(start, -start.getUTCDay());
}

export function endOfHstWeek(date: Date): Date {
  return new Date(addHstDays(startOfHstWeek(date), 7).getTime() - 1);
}

export function formatHstDisplay(
  date: string | Date | number | undefined | null,
  options: Intl.DateTimeFormatOptions = {},
): string {
  const dateObj = parseDateInput(date);
  if (!dateObj) {
    return '';
  }
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: HST_TIMEZONE,
    ...options,
  });
  return formatter.format(dateObj);
}

export function formatHstTime(
  date: string | Date | number | undefined | null,
  options: Intl.DateTimeFormatOptions = {},
): string {
  return formatHstDisplay(date, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...options,
  });
}

/**
 * Format a date string or Date object in HST timezone
 * @param date - ISO date string or Date object
 * @param formatStr - Format string: 'MMM d, yyyy', 'MMM d', 'MMM yyyy', or 'MMM d, yyyy HH:mm'
 * @returns Formatted date string in HST
 */
export function formatHstDate(date: string | Date, formatStr: string = 'MMM d, yyyy'): string {
  let dateObj: Date | null = null;
  if (typeof date === 'string' && isDateOnlyString(date)) {
    dateObj = parseHstDayKey(date);
  } else {
    dateObj = parseDateInput(date);
  }
  if (!dateObj) return '';

  const normalized = formatStr.toLowerCase();
  if (normalized === 'yyyy-mm-dd') return getHstDayKey(dateObj);

  const optionsByPattern: Record<string, Intl.DateTimeFormatOptions> = {
    'eee': { weekday: 'short' },
    'mmm': { month: 'short' },
    'mmm d': { month: 'short', day: 'numeric' },
    'mmm d, yyyy': { month: 'short', day: 'numeric', year: 'numeric' },
    'mmm yyyy': { month: 'short', year: 'numeric' },
    'mmmm yyyy': { month: 'long', year: 'numeric' },
    'eeee, mmmm d, yyyy': { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
    'mmm d, h:mm a': { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true },
    'mmm d, yyyy hh:mm': { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true },
    'mmm d, yyyy h:mm a': { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true },
    'mmm d, yyyy hh:mm a': { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true },
  };

  if (formatStr.includes('HH:mm')) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: HST_TIMEZONE,
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(dateObj);
    const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
    return `${value('month')} ${value('day')}, ${value('year')} ${value('hour')}:${value('minute')}`;
  }

  return new Intl.DateTimeFormat('en-US', {
    timeZone: HST_TIMEZONE,
    ...(optionsByPattern[normalized] ?? { month: 'short', day: 'numeric', year: 'numeric' }),
  }).format(dateObj);
}

/**
 * Format a date with time in HST timezone
 * @param date - ISO date string or Date object
 * @returns Formatted date and time string in HST, or empty string if invalid
 */
export function formatHstDateTime(date: string | Date | null | undefined): string {
  if (!date || (typeof date === 'string' && !date.trim())) {
    return '';
  }
  try {
    return formatHstDate(date, 'MMM d, yyyy HH:mm');
  } catch {
    return '';
  }
}

/**
 * Format a date as relative time (e.g., "2 hours ago") from HST perspective
 * @param date - ISO date string or Date object
 * @param options - formatDistanceToNow options
 * @returns Relative time string
 */
export function formatHstRelativeTime(date: string | Date, options?: { addSuffix?: boolean }): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  // Note: formatDistanceToNow doesn't support timezone directly, but we can
  // calculate the difference using the HST-adjusted date
  return formatDistanceToNow(dateObj, options);
}

/**
 * Format a date as a smart relative date for compact display
 * Returns formats like: "2h ago", "Yesterday", "Mon 3:45 PM", "Nov 15"
 * @param date - ISO date string or Date object
 * @returns Compact relative date string, or empty string if invalid
 */
export function formatHstDateRelative(date: string | Date | null | undefined): string {
  if (!date || (typeof date === 'string' && !date.trim())) {
    return '';
  }
  const dateObj = parseDateInput(date);
  if (!dateObj) {
    return '';
  }

  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Less than 1 hour: show minutes
  if (diffMins < 60) {
    if (diffMins < 1) return 'Just now';
    return `${diffMins}m ago`;
  }

  // Less than 24 hours: show hours
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  // Yesterday
  if (diffDays === 1) {
    return 'Yesterday';
  }

  // Within the last week: show day name
  if (diffDays < 7) {
    return formatHstDisplay(dateObj, { weekday: 'short' });
  }

  // Within the current year: show month and day
  const currentYear = now.getFullYear();
  const dateYear = dateObj.getFullYear();
  if (dateYear === currentYear) {
    return formatHstDisplay(dateObj, { month: 'short', day: 'numeric' });
  }

  // Different year: show month, day, and year
  return formatHstDisplay(dateObj, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Calendar-day distance in Hawaii; date-only inputs keep their stated day. */
export function differenceInHstCalendarDays(later: Date | string, earlier: Date | string): number {
  const dayStart = (value: Date | string) => {
    const date = typeof value === 'string' && isDateOnlyString(value)
      ? new Date(`${value}T10:00:00-00:00`) : new Date(value);
    return Number.isNaN(date.getTime()) ? NaN : startOfHstDay(date).getTime();
  };
  return Math.round((dayStart(later) - dayStart(earlier)) / 86400000);
}
